// The A/B group filter: read this dashboard for one arm of one experiment.
//
// It sits beside the date range and behaves like it — the selection lives in
// the URL, so a filtered dashboard is a link somebody can be sent. The
// experiment list, the ARMS (baseline first) and each option's live allocation
// all come from the experimentation registry (`GET /api/admin/experimentation`,
// one worker-built snapshot, 5-minute stale time), because a console that names
// experiments in its own source goes stale the day a third one ships and cannot
// tell "no data" from "not wired". EVERY kind is offered, and that is current:
// the comment here used to say product experiments only, which stopped being
// true when the boards learned to resolve a project-assigned arm through
// `recruiter.arm_filter`. Left uncorrected it is the kind of stale comment that
// invites a "fix" back to the narrower behaviour.
//
// Before the snapshot's first tick lands (`state === "computing"`) the A/B
// readout stands in as the registry, since it lists the same product
// experiments from the same code declarations. That is the ONLY time it is
// read here.
//
// Two honesty rules are enforced here rather than left to each page:
//
// - **A dark experiment says so.** While a dial sits at 0% nobody is assigned to
//   the experiment arm except testers who pinned themselves, so the option
//   carries the live percentage and the chip says "0% dialled" out loud. A
//   filter that silently selects an empty population looks exactly like a
//   feature that killed conversion.
// - **Both halves or neither.** An arm without an experiment names nothing, so
//   clearing either clears both.
// - **Only experiments that actually split are offered.** An experiment whose
//   whole population sits in ONE arm cannot narrow a board: picking it either
//   selects everybody (the arm holding 100%) or nobody (any other arm), and both
//   read as a finding rather than as an empty filter. That is one test, not two,
//   and it is taken from the SERVER-computed arm shares rather than from a dial
//   percentage, because a section's dial is inverted (100% is the shipped state,
//   not a full ramp) and a rule written against the number would hide the wrong
//   half of the board. The exception is the experiment the URL already names: a
//   link somebody was sent keeps working and keeps its warning, because dropping
//   the selection silently would change what the page says without saying so.

import { useQuery } from "@tanstack/react-query";

import {
  DEFAULT_ARMS,
  armLabel,
  baselineOf,
  type Arm,
  type ExperimentScope,
} from "../lib/experiment-scope";
import { dialledPct, splitsPopulation } from "../lib/experimentation";
import { abTestsQuery, experimentationQuery } from "../lib/queries";
import "./experiment-filter.css";

type Props = {
  scope: ExperimentScope | undefined;
  onChange: (next: ExperimentScope | undefined) => void;
};

type Option = {
  key: string;
  label: string;
  /** At least two arms hold somebody. False for a dial parked at either end. */
  splits: boolean;
  arms: readonly string[];
  pct: number;
  /** `user` for a product cohort, `project` for every other kind. */
  unit: string;
};

export function ExperimentFilter({ scope, onChange }: Props) {
  const registry = useQuery(experimentationQuery());
  const computing = registry.data?.state === "computing";
  // The fallback is enabled only while the registry has answered "computing":
  // a registry that is ready, or one that has not answered yet, never fires
  // this read.
  const readout = useQuery({ ...abTestsQuery(undefined, "all"), enabled: computing });

  const all: Option[] = computing
    ? (readout.data?.readout?.experiments ?? []).map((e) => ({
        key: e.name,
        label: e.label,
        arms: DEFAULT_ARMS,
        pct: e.pct,
        // The fallback readout carries a percentage and no arm shares, so the
        // ends of its dial are the only thing there is to test.
        splits: e.pct > 0 && e.pct < 100,
        unit: "user",
      }))
    : (registry.data?.experiments ?? [])
        .map((e) => ({
          key: e.key,
          label: e.label || e.key,
          arms: e.arms.length ? e.arms : DEFAULT_ARMS,
          pct: dialledPct(e.kind, e.allocation),
          splits: splitsPopulation(e),
          unit: e.unit,
        }))
        // A record with one arm has nothing to split, so offering it would be a
        // filter that cannot narrow anything.
        .filter((o) => o.arms.length > 1)
        .sort((a, b) => a.key.localeCompare(b.key));
  // Everything that can narrow the board, plus whatever the URL already names
  // even when it no longer can: a shared link keeps meaning what it meant.
  const options = all.filter((o) => o.splits || o.key === scope?.experiment);
  const hidden = all.length - options.length;
  const selected = options.find((o) => o.key === scope?.experiment);
  // Until the record has loaded (or for a retired experiment) the default two
  // arms are offered.
  const arms: readonly string[] = selected?.arms ?? DEFAULT_ARMS;

  return (
    <div className="ab-filter">
      <label className="ab-filter__field">
        <span className="ab-filter__label">A/B group</span>
        <select
          className="ab-filter__select"
          value={scope?.experiment ?? ""}
          disabled={options.length === 0}
          onChange={(event) => {
            const name = event.target.value;
            // Picking an experiment defaults to the BASELINE: it is the arm
            // with people in it, so the first thing a reader sees is a
            // populated funnel rather than an empty one.
            const picked = options.find((o) => o.key === name);
            onChange(
              name ? { experiment: name, arm: scope?.arm ?? baselineOf(picked?.arms) } : undefined,
            );
          }}
        >
          <option value="">
            {options.length === 0 && hidden > 0
              ? "Nothing is splitting traffic right now"
              : "Everyone (no split)"}
          </option>
          {options.map((option) => (
            <option key={option.key} value={option.key}>
              {option.label} ({option.pct}% dialled, per {option.unit})
            </option>
          ))}
        </select>
      </label>

      {hidden > 0 ? (
        <p className="ab-filter__hint">
          {hidden} {hidden === 1 ? "experiment is" : "experiments are"} not offered: every unit
          sits in one arm, so filtering by it would select everybody or nobody. Move a dial off
          the end in the console and it appears here.
        </p>
      ) : null}

      {scope ? (
        <div className="ab-filter__arms" role="group" aria-label="Arm">
          {arms.map((arm: Arm) => (
            <button
              key={arm}
              type="button"
              className={"ab-filter__arm" + (scope.arm === arm ? " ab-filter__arm--on" : "")}
              aria-pressed={scope.arm === arm}
              onClick={() => onChange({ experiment: scope.experiment, arm })}
            >
              {armLabel(arm, arms)}
            </button>
          ))}
        </div>
      ) : null}

      {scope && selected && !selected.splits ? (
        <p className="ab-filter__note">
          This experiment is parked at one end of its dial, so every unit sits in a single arm
          and the other arms hold only testers who pinned themselves. It is offered because the
          link you followed names it. Read the split as a smoke test, not as a result.
        </p>
      ) : null}

      {scope && selected && selected.unit !== "user" ? (
        <p className="ab-filter__note">
          This experiment buckets on the {selected.unit}, not the user, so its arms are
          populations of {selected.unit}s. A board that counts people reads that as the people
          who own them, which is close but not the same thing: check the denominator before
          quoting a rate off it.
        </p>
      ) : null}
    </div>
  );
}
